import { FullscreenModal } from 'shared/components/fullscreen-modal';
import { Text } from 'shared/components/text';
import { Button } from 'shared/components/button';
import { Search } from 'shared/@generated/models';
import { Platform, TouchableOpacity, View } from 'react-native';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import useApi from 'shared/library/use-api';
import { apiRoute } from 'shared/library/api-route';
import { InputError } from 'shared/components/form/input-error';
import { useTranslation } from 'react-i18next';
import { TextInput } from 'shared/components/form/text-input';
import { notify } from 'shared/library/notify';
import { ConfirmModal } from 'shared/components/confirm-modal';
import { BaseModalProps, BaseModalRef } from 'shared/components/base-modal';
import { useIsLoggedIn } from 'shared/state/user';
import { useLocation } from 'shared/library/use-location';
import { useRouter } from 'shared/library/use-router';
import { Switch } from 'shared/components/form/switch';
import useSearchesStore from 'shared/state/searches';
import { prepareDataForApi } from 'shared/library/api-client';
import { useShowLoginModalIfGuest } from 'shared/state/login';
import { analytics } from 'shared/library/analytics';
import { normalizeSearchData, type SearchData } from 'shared/library/saved-search-data';
import { savedSearchIntent } from 'shared/library/saved-search-intent';
import { SearchModalFilters } from 'shared/components/search-modal-filters';
import type { SearchFilterPanel } from 'shared/components/search-modal-filters.types';
import { useDevice } from 'shared/library/use-device';
import { FilterModalBackButton } from 'shared/components/web-filter-controls';

interface SearchModalProps extends BaseModalProps {
  uuid?: string;
  initialData?: Partial<SearchData>;
  source?: string;
  resultsCount?: number;
  boostedCount?: number;
  onSaved?: (search: Search) => void;
  onDeleted?: (uuid: string) => void;
}

export interface SearchModalRef extends BaseModalRef {}

export const SearchModal = forwardRef<SearchModalRef, SearchModalProps>(
  (
    {
      uuid,
      initialData,
      trigger,
      source = 'unknown',
      resultsCount,
      boostedCount,
      onSaved,
      onDeleted,
    },
    ref,
  ) => {
    const { t } = useTranslation('components');
    const { widerThanMd } = useDevice();
    const isLoggedIn = useIsLoggedIn();
    const router = useRouter();
    const showLoginModalIfGuest = useShowLoginModalIfGuest();
    const { request, errors, clearErrors, loading } = useApi();
    const modalRef = useRef<BaseModalRef>(null);
    const modalOpenRef = useRef(false);
    const resumedAfterLoginRef = useRef(false);
    const { location: userLocation } = useLocation();
    const addSearch = useSearchesStore((state) => state.add);
    const updateSearch = useSearchesStore((state) => state.update);
    const removeSearch = useSearchesStore((state) => state.remove);
    const isUpdating = !!uuid;
    const latitude = userLocation?.latitude ?? null;
    const longitude = userLocation?.longitude ?? null;
    const parsedInitialData = useMemo(
      () =>
        normalizeSearchData(initialData, {
          latitude,
          longitude,
        }),
      [initialData, latitude, longitude],
    );
    const [data, setData] = useState<SearchData>(parsedInitialData);
    const [draftRevision, setDraftRevision] = useState(0);
    const [filterPanel, setFilterPanel] = useState<SearchFilterPanel>('overview');
    const showingFilters = Platform.OS === 'web' && filterPanel !== 'overview';

    function getAnalyticsParams(extra?: Record<string, any>, searchData = data) {
      return {
        search_source: source,
        search_action: isUpdating ? 'update' : 'create',
        search_notify: searchData.notify,
        search_animal: searchData.animal,
        search_breed_type: searchData.breedType,
        search_gender: searchData.gender,
        search_pet_age: searchData.petAge,
        search_country: searchData.country,
        search_only_breeders: searchData.onlyBreeders,
        search_breeds_count: searchData.breeds?.length ?? 0,
        search_has_max_price: searchData.maxPrice !== null,
        search_has_distance: searchData.maxDistanceInKm !== null,
        results_count: resultsCount,
        boosted_count: boostedCount,
        search_resumed_after_login: resumedAfterLoginRef.current,
        ...extra,
      };
    }

    function trackOpen(isLoggedIn: boolean) {
      analytics.event('open_saved_search', getAnalyticsParams({ logged_in: isLoggedIn }));
    }

    function openModal() {
      if (!isLoggedIn) {
        trackOpen(false);
        const returnPath = savedSearchIntent.start(router.asPath, source, parsedInitialData);
        showLoginModalIfGuest({ source: 'search-modal' }, returnPath);
        return;
      }

      resumedAfterLoginRef.current = false;
      trackOpen(true);
      resetDraft();
      modalOpenRef.current = true;
      modalRef.current?.open();
    }

    function resetDraft(nextData = parsedInitialData) {
      setData(nextData);
      setDraftRevision((revision) => revision + 1);
      setFilterPanel('overview');
      clearErrors();
    }

    function showSearchForm() {
      setFilterPanel('overview');
    }

    function changeData(nextData: Partial<SearchData>) {
      setData((state) => ({ ...state, ...nextData }));
      Object.keys(nextData).forEach((field) => clearErrors(field));
    }

    function save() {
      request<{ search: Search }>(
        isUpdating ? apiRoute('searches.update', { search: uuid! }) : apiRoute('searches.store'),
        {
          ...prepareDataForApi(data),
          breeds: data.breeds!.map((breed) =>
            typeof breed === 'string'
              ? { breed_id: null, query: breed }
              : { breed_id: breed, query: null },
          ),
        },
      )
        .then((data) => {
          if (isUpdating) {
            updateSearch(data.search);
            onSaved?.(data.search);
            analytics.event('update_saved_search', getAnalyticsParams());
            notify.success();
          } else {
            addSearch(data.search);
            onSaved?.(data.search);
            analytics.event('create_saved_search', getAnalyticsParams());
            notify.success(t('search_modal.created_successfully'));
          }

          modalRef.current?.close();
        })
        .catch((err) => notify.error(err, 'search-modal.tsx - saveSearch'));
    }

    function deleteSearch() {
      return request(apiRoute('searches.destroy', { search: uuid! })).then(() => {
        removeSearch(uuid!);
        onDeleted?.(uuid!);
        modalRef.current?.close();
      });
    }

    useImperativeHandle(ref, () => ({
      open: openModal,
      close: () => modalRef.current?.close(),
    }));

    useEffect(() => {
      if (!modalOpenRef.current) setData(parsedInitialData);
    }, [parsedInitialData]);

    useEffect(() => {
      if (!isLoggedIn || isUpdating || !modalRef.current) return;

      const pendingData = savedSearchIntent.take(router.asPath, source);
      if (!pendingData) return;

      const nextData = normalizeSearchData(pendingData);
      resumedAfterLoginRef.current = true;
      resetDraft(nextData);
      modalOpenRef.current = true;
      analytics.event('open_saved_search', getAnalyticsParams({ logged_in: true }, nextData));
      modalRef.current.open();
    }, [isLoggedIn, isUpdating, router.asPath, source]);

    if (!isLoggedIn) {
      return (
        <>
          {trigger?.({
            open: openModal,
            close: () => {},
          })}
        </>
      );
    }

    const filterTitle =
      filterPanel === 'overview'
        ? t('listings_filter_form.all_filters')
        : {
            breeds: t('listings_filter_form.breed'),
            country: t('listings_filter_form.country'),
            animal: t('listings_filter_form.animal'),
            breedType: t('listings_filter_form.breed_type'),
            gender: t('listings_filter_form.gender'),
            seller: t('listings_filter_form.seller'),
            petAge: t('listings_filter_form.age'),
            maxPrice: t('listings_filter_form.max_price'),
            distance: t('listings_filter_form.location'),
          }[filterPanel];
    const formTitle = isUpdating
      ? t('search_modal.update_saved_search')
      : t('search_modal.create_saved_search');

    return (
      <FullscreenModal
        ref={modalRef}
        onClose={() => {
          modalOpenRef.current = false;
          resumedAfterLoginRef.current = false;
          resetDraft();
        }}
        centered={widerThanMd}
        showCloseButton={!showingFilters}
        scrollable={!showingFilters}
        animationInTiming={160}
        animationOutTiming={80}
        contentTestID={'search-modal'}
        headerTitle={showingFilters ? filterTitle : formTitle}
        description={showingFilters ? undefined : t('search_modal.description')}
        headerLeft={
          showingFilters
            ? () => (
                <FilterModalBackButton
                  label={t('common:back')}
                  testID={'search-filter-back'}
                  onPress={showSearchForm}
                />
              )
            : undefined
        }
        onRequestClose={() => {
          if (!showingFilters) {
            return true;
          }

          showSearchForm();
          return false;
        }}
        trigger={
          trigger
            ? (modal) =>
                trigger({
                  ...modal,
                  open: openModal,
                })
            : undefined
        }
        headerRight={() =>
          !showingFilters &&
          isUpdating && (
            <ConfirmModal
              onConfirm={deleteSearch}
              trigger={(modal) => (
                <TouchableOpacity
                  className={'rounded p-2 transition-colors hover:bg-red-50'}
                  accessibilityRole={'button'}
                  disabled={loading}
                  onPress={modal.open}
                >
                  <Text className={'text-sm font-bold text-red-700'}>{t('common:delete')}</Text>
                </TouchableOpacity>
              )}
            />
          )
        }
        footer={() => (
          <Button
            testID={showingFilters ? 'search-filter-apply' : 'search-save'}
            text={showingFilters ? t('search_modal.done') : t('common:save')}
            center={true}
            loading={loading}
            onPress={showingFilters ? showSearchForm : save}
          />
        )}
      >
        {showingFilters ? (
          <View className={'min-h-0 flex-1'} testID={'search-filter-modal'}>
            <SearchModalFilters
              key={draftRevision}
              data={data}
              panel={filterPanel}
              onPanelChange={setFilterPanel}
              onChange={changeData}
            />
          </View>
        ) : (
          <View testID={'search-form'}>
            <Text className={'text-brand-brown-dark font-bold'}>{t('search_modal.name')}</Text>
            <View className={'mt-1.5'}>
              <TextInput
                testID={'search-name-input'}
                value={data.name}
                onChangeText={(name: string) => {
                  setData((state) => ({ ...state, name }));
                  clearErrors('name');
                }}
                placeholder={t('search_modal.name_placeholder')}
              />
            </View>
            <InputError errors={errors} />

            <View className={'mt-6 flex-row items-center justify-between gap-4'}>
              <View className={'flex-1'}>
                <Text className={'text-brand-brown-dark font-bold'}>
                  {t('search_modal.notify')}
                </Text>
                <Text className={'text-sm text-stone-500'}>
                  {t('search_modal.notify_description')}
                </Text>
              </View>
              <Switch
                value={data.notify}
                size={'small'}
                accessibilityLabel={t('search_modal.notify')}
                onPress={(notify) => setData((state) => ({ ...state, notify }))}
              />
            </View>

            <View className={'mt-6'}>
              <Text className={'text-brand-brown-dark font-bold'}>{t('search_modal.filters')}</Text>
              <SearchModalFilters
                key={draftRevision}
                data={data}
                panel={'overview'}
                onPanelChange={setFilterPanel}
                onChange={changeData}
              />
            </View>
          </View>
        )}
      </FullscreenModal>
    );
  },
);

SearchModal.displayName = 'SearchModal';
